import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Switch,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  BADGE_CRITERIA_TYPES,
  criteriaTypeInfo,
  listBadges,
  createBadge,
  updateBadge,
  deleteBadge,
  apiErrorMessage,
} from "../../api/assessmentBadges";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const criteriaLabel = (value) => criteriaTypeInfo(value)?.label ?? value ?? "—";

const describeCriteria = (b) => {
  const parts = [];
  if (b.thresholdPercentage != null) parts.push(`${b.thresholdPercentage}%`);
  if (b.minCount != null) parts.push(`min ${b.minCount}`);
  return parts.length ? parts.join(" · ") : "—";
};

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "icon", label: "Icon", cellCss: { minWidth: "80px" }, getValue: (r) => r.icon },
  { key: "name", label: "Name", cellCss: { align: "left", textAlign: "left", minWidth: "180px" }, getValue: (r) => r.name },
  { key: "description", label: "Description", cellCss: { minWidth: "240px" }, getValue: (r) => r.description },
  { key: "criteriaType", label: "Criteria", cellCss: { minWidth: "200px" }, getValue: (r) => criteriaLabel(r.criteriaType) },
  { key: "threshold", label: "Threshold / Min Count", cellCss: { minWidth: "170px" }, getValue: (r) => describeCriteria(r) },
  { key: "studentsEarned", label: "Students Earned", cellCss: { align: "center", minWidth: "150px" }, getValue: (r) => r.studentsEarned },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

const STORAGE_KEY = "assessmentBadgesHiddenColumns";
const DEFAULT_HIDDEN = [];

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

// The backend stores `icon` as free text (an emoji or a short key) and leaves
// rendering to the client, so show exactly what is stored.
function GlyphBadge({ glyph }) {
  return (
    <Typography sx={{ fontSize: glyph ? "1.4rem" : "0.8rem", lineHeight: 1, color: glyph ? "inherit" : "text.secondary" }}>
      {glyph || "—"}
    </Typography>
  );
}

function BadgeFormDialog({ open, badge, onClose, onSave }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [criteriaType, setCriteriaType] = useState("");
  const [thresholdPercentage, setThresholdPercentage] = useState("");
  const [minCount, setMinCount] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setName(badge?.name || "");
    setDescription(badge?.description || "");
    setIcon(badge?.icon || "");
    setCriteriaType(badge?.criteriaType || "");
    setThresholdPercentage(badge?.thresholdPercentage ?? "");
    setMinCount(badge?.minCount ?? "");
    setStatus(badge?.status || "ACTIVE");
    setTouched({});
  }, [open, badge]);

  const info = criteriaTypeInfo(criteriaType);
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const thresholdInvalid =
    Boolean(info?.needsThreshold) &&
    (thresholdPercentage === "" || Number(thresholdPercentage) <= 0 || Number(thresholdPercentage) > 100);
  const minCountInvalid =
    Boolean(info?.needsMinCount) &&
    (minCount === "" || !Number.isInteger(Number(minCount)) || Number(minCount) < 1);
  const isValid = Boolean(
    name.trim() && description.trim() && icon.trim() && criteriaType && !thresholdInvalid && !minCountInvalid,
  );
  const emptyHelper = (key, value) =>
    touched[key] && !String(value).trim() ? "Please fill out this field" : undefined;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: badge?.id,
        name: name.trim(),
        description: description.trim(),
        icon: icon.trim(),
        criteriaType,
        thresholdPercentage: info?.needsThreshold ? Number(thresholdPercentage) : undefined,
        minCount: info?.needsMinCount ? Number(minCount) : undefined,
        isActive: status === "ACTIVE",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{badge ? "Edit Badge" : "Add Badge"}</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.25 }}>
        <TextField
          label="Name"
          required
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => touch("name")}
          error={Boolean(emptyHelper("name", name))}
          helperText={emptyHelper("name", name)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <TextField
          label="Description"
          required
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => touch("description")}
          error={Boolean(emptyHelper("description", description))}
          helperText={emptyHelper("description", description)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <TextField
          label="Icon"
          required
          fullWidth
          size="small"
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          onBlur={() => touch("icon")}
          error={Boolean(emptyHelper("icon", icon))}
          helperText={emptyHelper("icon", icon) || "An emoji (e.g. 🏆) or a short key, up to 50 characters"}
          inputProps={{ maxLength: 50 }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <FormControl fullWidth size="small" required error={Boolean(touched.criteriaType && !criteriaType)}>
          <InputLabel>Criteria Type</InputLabel>
          <Select
            label="Criteria Type *"
            value={criteriaType}
            onChange={(e) => setCriteriaType(e.target.value)}
            onClose={() => touch("criteriaType")}
          >
            {BADGE_CRITERIA_TYPES.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
          {touched.criteriaType && !criteriaType && <FormHelperText>Please fill out this field</FormHelperText>}
        </FormControl>
        {info?.needsThreshold && (
          <TextField
            label="Threshold Percentage"
            required
            type="number"
            fullWidth
            size="small"
            value={thresholdPercentage}
            onChange={(e) => setThresholdPercentage(e.target.value)}
            onBlur={() => touch("threshold")}
            error={Boolean(touched.threshold && thresholdInvalid)}
            helperText={
              touched.threshold && thresholdInvalid
                ? thresholdPercentage === ""
                  ? "Please fill out this field"
                  : "Must be between 0 and 100"
                : undefined
            }
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        )}
        {info?.needsMinCount && (
          <TextField
            label="Minimum Count"
            required
            type="number"
            fullWidth
            size="small"
            value={minCount}
            onChange={(e) => setMinCount(e.target.value)}
            onBlur={() => touch("minCount")}
            error={Boolean(touched.minCount && minCountInvalid)}
            helperText={
              touched.minCount && minCountInvalid
                ? minCount === ""
                  ? "Please fill out this field"
                  : "Must be a whole number of at least 1"
                : undefined
            }
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
            backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: status === "ACTIVE" ? "#2e7d32" : "#555" }}>
              {status === "ACTIVE" ? "Active" : "Inactive"}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
              {status === "ACTIVE" ? "Badge can be earned by students" : "Badge cannot be earned"}
            </Typography>
          </Box>
          <Switch
            checked={status === "ACTIVE"}
            onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontWeight: 600, color: "#616161" }}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={submitting}
          onClick={handleSubmit}
          disabled={!isValid}
          {...BUTTON_STYLES.primary}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentBadges() {
  const [badges, setBadges] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [criteriaFilter, setCriteriaFilter] = useState("");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const hidden = raw ? JSON.parse(raw) : DEFAULT_HIDDEN;
      if (Array.isArray(hidden)) {
        const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
        if (visible.length > 0) return new Set(visible);
      }
    } catch {}
    return new Set(allColumns.map((c) => c.key).filter((k) => !DEFAULT_HIDDEN.includes(k)));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadBadges = () => {
    setLoading(true);
    listBadges({
      page,
      size: rowsPerPage,
      isActive: statusFilter ? statusFilter === "ACTIVE" : undefined,
      criteriaType: criteriaFilter || undefined,
    })
      .then((data) => {
        setBadges(data.content || []);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setBadges([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load badges"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBadges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter, criteriaFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  // The backend has no search or sort parameters, so both act on the current page only.
  const sortedData = useMemo(() => {
    const filtered = badges.filter(
      (b) => !search || b.name?.toLowerCase().includes(search.toLowerCase()),
    );
    return filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [badges, search, orderBy, order]);

  const handleSort = (columnKey) => {
    if (columnKey === "threshold") return;
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (badge) => {
    setEditing(badge);
    setFormOpen(true);
  };

  const handleSave = ({ id, ...payload }) => {
    const request = id ? updateBadge(id, payload) : createBadge(payload);
    return request
      .then(() => {
        toast.success(id ? "Badge updated successfully!" : "Badge created successfully!");
        setFormOpen(false);
        loadBadges();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save badge"));
      });
  };

  const handleDelete = () => {
    deleteBadge(deleteId)
      .then(() => {
        toast.success("Badge deactivated!");
        setDeleteId(null);
        loadBadges();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to delete badge"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 480px", alignItems: "center" }}>
              <Tooltip title="Searches the badges on the current page only" arrow placement="top">
                <TextField
                  size="small"
                  placeholder="Search names"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220 }}
                />
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Criteria</InputLabel>
                <Select
                  label="Criteria"
                  value={criteriaFilter}
                  onChange={(e) => {
                    setCriteriaFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All Criteria</MenuItem>
                  {BADGE_CRITERIA_TYPES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 220, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Badge
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    {c.key === "icon" || c.key === "threshold" ? (
                      c.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === c.key}
                        direction={orderBy === c.key ? order : "asc"}
                        onClick={() => handleSort(c.key)}
                        sx={{
                          color: "#ffffff !important",
                          "&.Mui-active": { color: "#ffffff !important" },
                          "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                        }}
                      >
                        {c.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No badges found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "icon") return <GlyphBadge glyph={row.icon} />;
                          if (c.key === "name") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.name}</Typography>;
                          }
                          if (c.key === "description") {
                            return (
                              <Tooltip title={row.description || ""} arrow disableHoverListener={!row.description}>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "text.secondary",
                                    maxWidth: 260,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.description || "—"}
                                </Typography>
                              </Tooltip>
                            );
                          }
                          if (c.key === "studentsEarned") return (row.studentsEarned ?? 0).toLocaleString("en-IN");
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          return c.getValue(row) ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit Badge" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Badge" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(row.id)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <BadgeFormDialog open={formOpen} badge={editing} onClose={() => setFormOpen(false)} onSave={handleSave} />

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Deleting a badge deactivates it, so it is no longer awarded. You can reactivate it by editing the badge."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentBadges;
